import { Component, OnInit, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import * as moment from 'moment-timezone';

import { UserSite } from '../../../shared/models/UserSite';
import { UserAuthorFollowSite } from '../../model/UserAuthorFollowSite';
import { UserEventFollowSite } from '../../model/UserEventFollowSite';
import { UserNewsletterSite } from '../../model/UserNewsletterSite';
import { map, catchError } from "rxjs/operators";

import { AuthorFollowsService } from '../../services/authorFollows.service';
import { ExecuteService } from '../../../shared/services/executeService.service';
import { EventFollowsService } from '../../services/eventFollows.service';
import { UserNewslettersService } from '../../services/userNewsletters.service';
import { UsersSiteService } from '../../../shared/services/usersSite.service';
import { PaymentTransactionsService } from '../../services/paymentTransactions.service';
import { UrlService } from '../../../shared/services/url.service';
import { FormGroup } from '@angular/forms';
import { AlertService } from '../../../shared/alert/services/alert.service';
import { MatDialog } from "@angular/material";
import { DeleteUserModalComponent } from './deleteUserModal.component';
import { UserDeleteService } from '../../services/userDelete.service';
import { TransactionModalComponent } from './transactionModal.component';
import { UserPaymentTransactionSite } from '../../model/UserPaymentTransactionSite';
import { TransactionType } from '../../../paymentTransactions/models/TransactionType';
import { APP_SETTINGS } from '../../../shared/models/backofficeConfiguration';
import { ErrorHandlerService } from "../../../shared/services/errorHandler.service";
import { DiscordService } from '../../services/discordService.service';
import { DiscordMember } from '../../../shared/models/DiscordMember';

@Component({
  selector: 'app-usersSite',
  templateUrl: '../templates/usersSite.component.html'
})

@Injectable()
export class UsersSiteComponent implements OnInit {
  title = 'User';
  DashboardUrl: string;
  IsEdition: boolean = false;

  userModel: UserSite = {
    UserId: '',
    Name: '',
    Surname: '',
    UserName: '',
    Email: '',
    IsPremium: false,
    Phone: '',
    PremiumExpirationDateUtc: null,
    IsEmailConfirmed: false,
  };

  purchaseModel = {
    TokenId: null,
    SubscriptionId: null,
    PlatformId: null,
    PlatformType: null,
    Loading: false,
    Success: false,
    Error: false,
    Iddle: true,
    MessageOptions: {
      SubscriptionNotCharged:"Subscription Not Charged",
      EventDoesNotExists:"Payment not received",
      EventNotArchived:"Payment associated with another user ",
      SubscriptionNotActive: "Subscription not active",
      InvalidPurchase: "Invalid Purchase Token"
    },
    Message: ""
  };
  
  authorFollowsModel: UserAuthorFollowSite[] = null;
  eventFollowsModel: UserEventFollowSite[] = null;
  userNewsletterModel: UserNewsletterSite[] = null;
  paymentTransactionsModel: UserPaymentTransactionSite[] = [];

  discordMemberModel: DiscordMember = {
    UserId: '',
    MemberId: '',
  };

  constructor(
    private executeService: ExecuteService,
    private activatedRoute: ActivatedRoute,
    private usersSiteService: UsersSiteService,
    private authorFollowService: AuthorFollowsService,
    private eventFollowService: EventFollowsService,
    private userNewslettersService: UserNewslettersService,
    private paymentTransactionsService: PaymentTransactionsService,
    private alertService: AlertService,
    private urlService: UrlService,
    private userDeleteService: UserDeleteService,
    private discordService: DiscordService,
    private errorHandler: ErrorHandlerService,
    private dialog: MatDialog) {};

  ngOnInit() {
    this.Init();
  };

  Init(): void {
    this.DashboardUrl = this.urlService.GetDashboardRoute();
    this.activatedRoute.params.first(f => typeof f["userId"] != 'undefined').subscribe(params => { this.initModel(params["userId"]); });
    moment.tz.setDefault("UTC");
  }

  mergeTransactions(data: UserPaymentTransactionSite[]) {
    const merge = this.paymentTransactionsModel.concat(data);
    this.paymentTransactionsModel = merge.sort((a, b) => a.dateUtc > b.dateUtc ? -1 : 1);
  }

  initModel(userId: string) {
    this.usersSiteService.GetById(userId)
      .subscribe(data => {
        if (data !== null) {
          this.userModel = data;
          this.authorFollowService.GetAuthorFollowsByEmail(data.Email)
            .subscribe(data => {
              this.authorFollowsModel = data;
            });

          this.eventFollowService.GetEventFollowsByEmail(data.Email)
            .subscribe(data => {
              this.eventFollowsModel = data;
            });

          this.userNewslettersService.GetUserNewslettersByEmail(data.Email)
            .subscribe(data => {
              this.userNewsletterModel = data;
            });

          this.paymentTransactionsService.GetPaymentTransactionsByUserIdFromPay(userId)
            .subscribe(data => {
              this.mergeTransactions(data);
            })

          this.discordService.GetMemberByUserId(userId)
            .subscribe(data => {
              if(data) {
                this.discordMemberModel = data;
              } else {
                this.discordMemberModel = { MemberId: '', UserId: ''};
              }
            })
        }
      });
  }

  Save(form: FormGroup) {
    this.alertService.removeAll();
    this.userModel.IsPremium = this.userModel.PremiumExpirationDateUtc.isAfter();
    window.scrollTo(0, 0);
    if (!form.valid) {
      this.alertService.add('danger', 'There are some errors in the form.');
      return;
    }
    this.usersSiteService.Edit(this.userModel.UserId, this.userModel)
      .subscribe(() => this.editSuccess(), err => this.alertService.add('danger', 'Edit UserSite error: ' + err));
  }

  private editSuccess() {
    this.alertService.add('success', 'The user has been edited.');
  };

  downloadJson() {
    const data = {
      ...this.userModel,
      authorsFollowing: this.authorFollowsModel.map(af => ({ Id: af.AuthorId, Name: af.AuthorName })),
      eventsFollowing: this.eventFollowsModel.map(ef => ({ Id: ef.PostId, Title: ef.PostTitle })),
      newslettersSubscribed: this.userNewsletterModel.map(un => ({ Id: un.NewsletterId, Name: un.NewsletterName })),
      subscriptionPayments: this.paymentTransactionsModel
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });

    saveAs(blob, `${this.userModel.UserName}.json`);
  }

  shouldShowDownload(): boolean {
    return this.userModel.UserId != '' && this.authorFollowsModel != null
      && this.eventFollowsModel != null && this.userNewsletterModel != null
      && this.paymentTransactionsModel != null;
  }

  showTransactionDetails(transaction: UserPaymentTransactionSite) {

    this.dialog.open(TransactionModalComponent, {
      hasBackdrop: true,
      backdropClass: 'fxstreet-modal-backdrop',
      panelClass: 'fxstreet-preview-dialog-panel',
      position: { top: '150px' },
      data: {
        detailsJson: transaction.details
      }
    })
  }

  getTransactionRowClass(transaction: UserPaymentTransactionSite) {
    switch (transaction.type) {
      case TransactionType.SubscriptionCreated:
      case TransactionType.SinglePayment:
        return "success";
      case TransactionType.SubscriptionCanceled:
        return "danger";
      case TransactionType.SubscriptionPaid:
        return "info";
      case TransactionType.SubscriptionExpired:
      case TransactionType.SubscriptionFailed:
      case TransactionType.SubscriptionRefunded:
        return "warning";
      default:
        return "";
    }
  }

  getPeriodText(transaction: UserPaymentTransactionSite) {
    switch (transaction.subscription.months) {
      case 12:
        return 'Yearly'
      case 3:
        return 'Quarterly'
      case 1:
        return 'Monthly'
      case 0:
        return ''
      default:
        return `${transaction.subscription.months} months`
    }
  }

  delete() {

    this.dialog.open(DeleteUserModalComponent, {
      hasBackdrop: true,
      backdropClass: 'fxstreet-modal-backdrop',
      panelClass: 'fxstreet-preview-dialog-panel'
    }).afterClosed().subscribe(shouldDelete => {
      if (shouldDelete)
        this.deleteUser(this.userModel.Email);
    });
  }

  deleteUser(email) {
    if (email && email !== '') {
      this.userDeleteService.DeleteUser(email)
        .subscribe(
          res => { this.alertService.add('success', 'The user has been successfully deleted.') },
          error => { this.alertService.add('danger', 'The user could not be deleted.'); console.log(error); }
        );
    }
  }

  private getBody(userModel, purchaseModel) {
    let body: {
      [key: string]: any
    } = {
      'userId': userModel.UserId,
      'subscriptionId': purchaseModel.SubscriptionId,
      'email': this.userModel.Email
    }
    if (this.purchaseModel.PlatformType === "apple") {
      body.appleTransactionId = purchaseModel.PlatformId;
    } else {
      body.googleSubscriptionId = purchaseModel.PlatformId;
      body.purchaseToken = purchaseModel.TokenId;
    };
    return body;
  };

  processPurchaseToken() {
    this.purchaseModel.Iddle = false;
    this.purchaseModel.Loading = true;
    this.purchaseModel.Success = false;
    this.purchaseModel.Error = false;

    let url = APP_SETTINGS.UserPayApiUrl + "/api/v1/paymentTransaction/";
    url += this.purchaseModel.PlatformType === "apple" ? "RecoverPremiumAccessApple" : "RecoverPremiumAccessGoogle";
    const body = this.getBody(this.userModel, this.purchaseModel);

    this.executeService.ExecuteWithToken('post', url, null, body).pipe(
      map((response) => {
        this.purchaseModel.Loading = false;
        if (response.isSuccess) {
          this.purchaseModel.Success = true;
          this.purchaseModel.Loading = false;
          this.purchaseModel.Error = false;
          this.purchaseModel.Iddle = false;
        } else {
          this.purchaseModel.Success = false;
          this.purchaseModel.Loading = false;
          this.purchaseModel.Error = true;
          this.purchaseModel.Iddle = false;
          this.purchaseModel.Message = this.purchaseModel.MessageOptions[response.message];
        }
      }),
      catchError(this.errorHandler.handleError<object[]>('Platform Update Purchase Error', [], true))
    ).subscribe(
      (next) => { console.log(next); },
      (error) => { console.log(error); }
    );    
  };
};



// WEBPACK FOOTER //
// ./src/app/users/components/scripts/usersSite.component.ts